import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function TermSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      type: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: organizationId,
    },
  });

  const [termsData, setTermsData] = useState([]);

  const fetchTerms = async () => {
    try {
      const response = await get(
        `StudentEvaluation/GetAllEvaluationCriteriaType?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTermsData(response?.evaluationTypes);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, [triggerRerender]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "type",
        header: "Name",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEditClick(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDeleteClick(row.original.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = termsData.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.type,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TermSetup");
    XLSX.writeFile(workbook, "TermSetup.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleId == "2" ? orgLogo : roleId == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Criteria Type / Term Setup";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) =>
        column.accessorKey !== "actions" &&
        column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = termsData.map((row, index) => {
      return [
        index + 1,
        ...includedColumns.map((column) => row[column.accessorKey] || ""),
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("TermSetup.pdf");
  };

  const handleEditClick = async (rowId) => {
    const response = await get(
      `StudentEvaluation/GetEvaluatinCriteriaTypeById?id=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const evalutionTypeSingle = response?.evalutionType;

    reset({
      id: evalutionTypeSingle.id,
      type: evalutionTypeSingle.type,
      organizationId: evalutionTypeSingle.organizationId,
      createdBy: evalutionTypeSingle.createdBy,
      updatedBy: localStorage.getItem("userId"),
    });
  };

  const handleDeleteClick = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this record?"
    );

    if (confirmDelete) {
      try {
        const response = await get(
          `StudentEvaluation/DeleteEvaluationCriteriaTypeById?id=${rowId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.isSuccess) {
          setTriggerRerender((prev) => !prev);
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post(
        "StudentEvaluation/AddUpdateEvaluationCriteriaType",
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          type: "",
          organizationId: organizationId,
          createdBy: localStorage.getItem("userId"),
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Criteria Type / Term Setup</h2>
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label">
                    Term Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`type`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("type")}
                  />
                  {errors.type && (
                    <small className="text-danger">{errors.type.message}</small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={termsData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default TermSetup;
